import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { useIntl } from 'react-intl';
import { useCurrency } from '../../../../hooks/useCurrency';

const PracticeDetailsSection = ({ user, company }) => {
  const intl = useIntl();
  const { symbol: currencySym } = useCurrency();
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'N/A';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return 'N/A';

    if (num >= 1000000) {
      return `${currencySym}${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `${currencySym}${(num / 1000).toFixed(0)}K`;
    }
    return `${currencySym}${num.toLocaleString()}`;
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return 'N/A';
    const num = typeof value === 'string' ? parseInt(value) : value;
    if (isNaN(num)) return 'N/A';
    return num.toLocaleString();
  };

  // Product Mix Donut Chart Component
  const ProductMixChart = ({ data, title }) => {
    if (!data || data.length === 0) return null;

    const total = data.reduce((sum, item) => sum + (item.value || 0), 0);
    if (total === 0) return null;

    const colors = [
      'hsl(var(--succession-primary))',
      'hsl(var(--succession-secondary))',
      'hsl(var(--succession-accent))',
      'hsl(var(--succession-tertiary))',
      '#8b5cf6',
      '#f59e0b',
      '#ef4444',
      '#10b981'
    ];

    const size = 160;
    const strokeWidth = 20;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    let currentOffset = 0;

    return (
      <div className="space-y-3">
        <h5 className="text-sm font-semibold text-succession-text-primary text-center">{title}</h5>
        <div className="flex flex-col items-center gap-3">
          <div className="relative" style={{ width: size, height: size }}>
            <svg viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
              {/* Background circle */}
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="rgba(128, 128, 128, 0.1)"
                strokeWidth={strokeWidth}
              />
              {/* Data segments */}
              {data.map((item, index) => {
                const percent = (item.value / total) * 100;
                const dash = (percent / 100) * circumference;
                const segment = (
                  <circle
                    key={index}
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={colors[index % colors.length]}
                    strokeWidth={strokeWidth}
                    strokeDasharray={`${dash} ${circumference - dash}`}
                    strokeDashoffset={currentOffset}
                  />
                );
                currentOffset -= dash;
                return segment;
              })}
            </svg>
            {/* Center total */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-lg font-bold text-succession-text-primary">
                  {formatCurrency(total)}
                </div>
                <div className="text-xs text-succession-text-muted">{intl.formatMessage({ id: 'succession.profile_drawer.practice_details.total_label' })}</div>
              </div>
            </div>
          </div>
          {/* Legend */}
          <div className="w-full space-y-1">
            {data.map((item, index) => {
              const percent = ((item.value / total) * 100).toFixed(1);
              return (
                <div key={index} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: colors[index % colors.length] }}
                    />
                    <span className="text-succession-text-muted">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-succession-text-primary font-medium">{formatCurrency(item.value)}</span>
                    <span className="text-succession-text-muted">({percent}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Build product mix data
  const buildProductMixData = () => {
    const productMix = user.product_mix || {};
    
    // Insurance products (for P&C)
    const insuranceData = [];
    if (productMix.life_ifp) insuranceData.push({ label: intl.formatMessage({ id: 'succession.profile_drawer.practice_details.life_insurance' }), value: productMix.life_ifp });
    if (productMix.home_ifp) insuranceData.push({ label: intl.formatMessage({ id: 'succession.profile_drawer.practice_details.home_insurance' }), value: productMix.home_ifp });
    if (productMix.auto_ifp) insuranceData.push({ label: intl.formatMessage({ id: 'succession.profile_drawer.practice_details.auto_insurance' }), value: productMix.auto_ifp });
    if (productMix.commercial_ifp) insuranceData.push({ label: intl.formatMessage({ id: 'succession.profile_drawer.practice_details.commercial_insurance' }), value: productMix.commercial_ifp });
    if (productMix.health_ifp) insuranceData.push({ label: intl.formatMessage({ id: 'succession.profile_drawer.practice_details.health_insurance' }), value: productMix.health_ifp });
    if (productMix.group_ifp) insuranceData.push({ label: intl.formatMessage({ id: 'succession.profile_drawer.practice_details.group_insurance' }), value: productMix.group_ifp });

    // Wealth management products
    const wealthData = [];
    if (productMix.mutual_funds_aum) wealthData.push({ label: intl.formatMessage({ id: 'succession.profile_drawer.practice_details.mutual_funds' }), value: productMix.mutual_funds_aum });
    if (productMix.securities_aum) wealthData.push({ label: intl.formatMessage({ id: 'succession.profile_drawer.practice_details.securities' }), value: productMix.securities_aum });
    if (productMix.annuities_aua) wealthData.push({ label: intl.formatMessage({ id: 'succession.profile_drawer.practice_details.annuities' }), value: productMix.annuities_aua });
    if (productMix.segregated_funds_aum) wealthData.push({ label: intl.formatMessage({ id: 'succession.profile_drawer.practice_details.segregated_funds' }), value: productMix.segregated_funds_aum });

    return { insuranceData, wealthData };
  };

  const { insuranceData, wealthData } = buildProductMixData();
  const hasProductMix = insuranceData.length > 0 || wealthData.length > 0;

  return (
    <div className="space-y-6">
      {/* Team Information */}
      {user.has_team && user.team && (
        <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
          <h3 className="text-xl font-semibold text-succession-text-primary mb-4">{intl.formatMessage({ id: 'succession.profile_drawer.practice_details.team_information' })}</h3>
          <div className="space-y-3">
            {user.team.teamDescription && (
              <div>
                <h5 className="text-sm font-semibold text-succession-text-primary mb-1">{intl.formatMessage({ id: 'succession.profile_drawer.practice_details.description' })}</h5>
                <p className="text-sm text-succession-text-muted whitespace-pre-line">{user.team.teamDescription}</p>
              </div>
            )}
            {user.number_of_employees && (
              <div>
                <h5 className="text-sm font-semibold text-succession-text-primary mb-1">{intl.formatMessage({ id: 'succession.profile_drawer.practice_details.team_size' })}</h5>
                <p className="text-sm text-succession-text-muted">{user.number_of_employees} {intl.formatMessage({ id: 'succession.profile_drawer.practice_details.employees_suffix' })}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Practice Details */}
      <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
        <h3 className="text-xl font-semibold text-succession-text-primary mb-4">{intl.formatMessage({ id: 'succession.profile_drawer.practice_details.practice_details' })}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {user.niche_market && (
            <div>
              <h5 className="text-sm font-semibold text-succession-text-primary mb-1">{intl.formatMessage({ id: 'succession.profile_drawer.practice_details.niche_market' })}</h5>
              <p className="text-sm text-succession-text-muted">{user.niche_market}</p>
            </div>
          )}
          {user.average_client_age && (
            <div>
              <h5 className="text-sm font-semibold text-succession-text-primary mb-1">{intl.formatMessage({ id: 'succession.profile_drawer.practice_details.average_client_age' })}</h5>
              <p className="text-sm text-succession-text-muted">{user.average_client_age} {intl.formatMessage({ id: 'succession.profile_drawer.practice_details.years_suffix' })}</p>
            </div>
          )}
          {user.part_of_team !== undefined && (
            <div>
              <h5 className="text-sm font-semibold text-succession-text-primary mb-1">{intl.formatMessage({ id: 'succession.profile_drawer.practice_details.part_of_team' })}</h5>
              <p className="text-sm text-succession-text-muted">{user.part_of_team ? intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.true' }) : intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.false' })}</p>
            </div>
          )}
          {user.team_size && (
            <div>
              <h5 className="text-sm font-semibold text-succession-text-primary mb-1">{intl.formatMessage({ id: 'succession.profile_drawer.practice_details.team_members' })}</h5>
              <p className="text-sm text-succession-text-muted">{user.team_size} {intl.formatMessage({ id: 'succession.profile_drawer.practice_details.members_suffix' })}</p>
            </div>
          )}
        </div>
      </div>

      {/* Product Mix Charts */}
      {hasProductMix && (
        <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
          <h3 className="text-xl font-semibold text-succession-text-primary mb-6">{intl.formatMessage({ id: 'succession.profile_drawer.practice_details.product_mix' })}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {insuranceData.length > 0 && (
              <ProductMixChart data={insuranceData} title={intl.formatMessage({ id: 'succession.profile_drawer.practice_details.insurance_products' })} />
            )}
            {wealthData.length > 0 && (
              <ProductMixChart data={wealthData} title={intl.formatMessage({ id: 'succession.profile_drawer.practice_details.wealth_management_products' })} />
            )}
          </div>
        </div>
      )}

      {/* Affiliations */}
      {user.affiliations && user.affiliations.length > 0 && (
        <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
          <h3 className="text-xl font-semibold text-succession-text-primary mb-4">{intl.formatMessage({ id: 'succession.profile_drawer.practice_details.affiliations' })}</h3>
          <div className="flex flex-wrap gap-2">
            {user.affiliations.map((affiliation, index) => {
              // Handle both string and object formats from GraphQL
              const affiliationText = typeof affiliation === 'string' ? affiliation : affiliation?.name || affiliation?.id || '';
              return (
                <span
                  key={affiliation?.id || index}
                  className="inline-flex items-center px-3 py-1 rounded-md text-sm bg-succession-bg-primary border border-succession-border text-succession-text-primary"
                >
                  {affiliationText}
                </span>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};

PracticeDetailsSection.propTypes = {
  user: PropTypes.shape({
    has_team: PropTypes.bool,
    team: PropTypes.object,
    number_of_employees: PropTypes.number,
    niche_market: PropTypes.string,
    average_client_age: PropTypes.number,
    part_of_team: PropTypes.bool,
    team_size: PropTypes.number,
    product_mix: PropTypes.object,
    affiliations: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  company: PropTypes.object,
};

export default PracticeDetailsSection;

